import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppComponent } from './app.component';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { MarkantElementsModule } from '@markant/elements-angular';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { AuthClientConfig, AuthModule } from '@auth0/auth0-angular';

import { AppRoutingModule } from './app-routing.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LogoutPageComponent } from './logout-page/logout-page.component';
import {
  AppConfigService,
  AppConfiguration,
  CMSLanguageService,
  DefaultLocale,
  featureStateName,
  FilterEffects,
  filterReducer,
  LocalesArray,
} from '@markant-platform/shared/data-access';
import { LocaleService } from '@markant-platform/shared/util-locale';
import { TranslocoService } from '@jsverse/transloco';
import { TranslocoService as TranslocoServiceNgneat } from '@ngneat/transloco';
import { LocaleHeaderInterceptor } from './interceptor/locale-header.interceptor';
import { PlatformAuthHttpInterceptor } from './interceptor/platform-auth-http.interceptor';
import { RegistrationHeaderComponent, SharedRegistrationModule } from '@markant-platform/shared/registration';
import { VersionMismatchInterceptor } from './interceptor/version-mismatch.interceptor';
import { PlatformErrorHandler } from './error-handler/platform-error-handler';
import { SharedFooterModule } from '@markant-platform/shared/footer';
import { ModalComponent } from '@markant-platform/shared/ui-modal';
import { CmsPageStaticComponent } from './cms-page-static/cms-page-static.component';
import { DATE_PIPE_DEFAULT_OPTIONS, registerLocaleData } from '@angular/common';
import { LocaleId } from './localization';
import localeDe from '@angular/common/locales/de';
import { MfaSelectPageComponent } from './mfa-select-page/mfa-select.page.component';
import { RetryInterceptor } from './interceptor/retry.interceptor';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { ServerErrorInterceptor } from './interceptor/server-error.interceptor';
import { TranslocoRootModuleNgneat } from './transloco-root-ngneat.module';
import { TranslocoRootModule } from './transloco-root.module';
import { NgHcaptchaModule } from 'ng-hcaptcha';
import { BypassSecurityHtmlPipe } from '@markant-platform/shared/util-pipes';

export function initializeAppConfig(
  appConfigService: AppConfigService,
  authClientConfig: AuthClientConfig
): () => Promise<AppConfiguration> {
  return () =>
    appConfigService.fetchAppConfiguration().then((appConfig: AppConfiguration) => {
      // only after fetching the app configuration the auth client can be configured
      authClientConfig.set(appConfig.auth);
      // build remote routes
      return appConfig;
    });
}

export function initializeLanguage(
  translocoService: TranslocoService,
  translocoServiceNgneat: TranslocoServiceNgneat,
  cmsLanguageService: CMSLanguageService
): () => void {
  return () => {
    const isStoredLanguageSupported = LocalesArray.map(locale => locale.shorthand).includes(
      localStorage.getItem('language') || ''
    );
    const initialLang = isStoredLanguageSupported
      ? (localStorage.getItem('language') as string)
      : DefaultLocale.shorthand;

    translocoService.setActiveLang(initialLang);
    translocoServiceNgneat.setActiveLang(initialLang);
    cmsLanguageService.activeCMSLanguage = initialLang;
  };
}

registerLocaleData(localeDe);

@NgModule({
  declarations: [AppComponent, LogoutPageComponent, CmsPageStaticComponent, MfaSelectPageComponent],
  imports: [
    BrowserModule,
    AuthModule.forRoot(), // client config will be set in the `initializeAppConfig` function
    StoreModule.forRoot(
      { [featureStateName]: filterReducer },
      {
        metaReducers: [],
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: true,
        },
      }
    ),
    EffectsModule.forRoot([FilterEffects]),
    StoreRouterConnectingModule.forRoot(),
    !environment.production ? StoreDevtoolsModule.instrument({ connectInZone: true }) : [],
    HttpClientModule,
    TranslocoRootModule,
    TranslocoRootModuleNgneat,
    AppRoutingModule,
    MarkantElementsModule.forRoot({
      currencyLocale: 'de-DE',
      locale: 'de-DE',
    }),
    BrowserAnimationsModule,
    SharedRegistrationModule,
    SharedFooterModule,
    ModalComponent,
    RegistrationHeaderComponent,
    NgHcaptchaModule.forRoot(),
    BypassSecurityHtmlPipe,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppConfig,
      deps: [AppConfigService, AuthClientConfig],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeLanguage,
      deps: [TranslocoService, TranslocoServiceNgneat, CMSLanguageService],
      multi: true,
    },
    provideCharts(withDefaultRegisterables()),
    { provide: HTTP_INTERCEPTORS, useClass: PlatformAuthHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LocaleHeaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: VersionMismatchInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: RetryInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ServerErrorInterceptor, multi: true },
    { provide: ErrorHandler, useClass: PlatformErrorHandler },
    LocaleService,
    { provide: LOCALE_ID, useClass: LocaleId, deps: [TranslocoService] },
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { dateFormat: 'dd.MM.yyyy' } },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
